import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Save,
    ArrowLeft,
    Loader2,
    Calendar,
    Briefcase,
    Users,
    CheckCircle2,
    MessageSquare,
    Hash,
    AlertCircle
} from 'lucide-react';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import CustomSelect from '../../components/Common/Select/CustomSelect';
import PaginatedSelect from '../../components/Common/Select/PaginatedSelect';
import CustomDatePicker from '../../components/Common/DatePicker/CustomDatePicker';
import styles from './TaskForm.module.css';
import NotFound from '../Error/NotFound';


const EditTask = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');



    // Form State
    const [formData, setFormData] = useState({
        project_id: null,
        title: '',
        description: '',
        priority: null,
        status: null,
        due_date: '',
        employee_ids: []
    });

    const fetchInitialData = useCallback(async () => {
        setLoading(true);
        try {
            const taskResult = await api.get(`/tasks/${id}`);

            if (taskResult.data.success) {
                const task = taskResult.data.data;

                // Authorization check: Only creator or admin can edit
                if (task.created_by !== currentUser.id && !currentUser.is_admin) {
                    toast.error('You do not have permission to edit this task');
                    navigate('/tasks');
                    return;
                }

                setFormData({
                    project_id: task.project_id || '',
                    title: task.title || '',
                    description: task.description || '',
                    priority: { value: task.priority, label: task.priority.charAt(0).toUpperCase() + task.priority.slice(1) },
                    status: { value: task.status, label: task.status.replace('_', ' ').toUpperCase() },
                    due_date: task.due_date || '',
                    employee_ids: task.assigned_employees?.map(e => e.id) || []
                });
            } else {
                console.error('Error fetching task details');
                toast.error('Failed to load task details');
            }
        } catch (error) {
            console.error('Error fetching edit data:', error);
            toast.error('Failed to load task details');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                ...formData,
                project_id: formData.project_id,
                priority: formData.priority?.value,
                status: formData.status?.value,
                employee_ids: formData.employee_ids
            };

            const response = await api.put(`/tasks/${id}`, payload);
            if (response.data.success) {
                toast.success('Task updated successfully');
                navigate('/tasks');
            }
        } catch (error) {
            console.error('Error updating task:', error);
            toast.error(error.response?.data?.message || 'Failed to update task');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className={styles['task-form-container']}>
                <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
                <div className={styles['content-wrapper']}>
                    <Header onMenuClick={() => setIsSidebarOpen(true)} />
                    <div className={styles['loading-state']}>
                        <Loader2 className={styles['spinner']} />
                        <p>Syncing task data...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!formData.title) {
        return <NotFound />;
    }


    return (
        <div className={styles['task-form-container']}>
            <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
            {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

            <div className={styles['content-wrapper']}>
                <Header onMenuClick={() => setIsSidebarOpen(true)} />

                <main className={styles['main-content']}>
                    <Breadcrumbs />

                    <div className={styles['page-header']}>
                        <div className={styles['header-title']}>
                            <h1>Edit Task</h1>
                            <p>Update task details, assignments, and progress.</p>
                        </div>
                        <button className={styles['btn-cancel']} onClick={() => navigate('/tasks')}>
                            <ArrowLeft size={18} />
                            <span>Back to Tasks</span>
                        </button>
                    </div>

                    <div className={styles['form-card']}>
                        <form onSubmit={handleSubmit} className={styles['form-grid']}>
                            <div className={`${styles['form-group']} ${styles['full-width']}`}>
                                <label><Briefcase size={16} /> Associated Project <span className={styles['required']}>*</span></label>
                                <PaginatedSelect
                                    endpoint="/projects-dropdown"
                                    valueKey="id"
                                    labelKey="project_name"
                                    value={formData.project_id}
                                    onChange={(val) => setFormData(prev => ({ ...prev, project_id: val }))}
                                    placeholder="Select point of contact..."
                                    formatOptionLabel={(opt) => `${opt.label} (${opt.original?.project_code || 'PRJ'})`}
                                />
                            </div>

                            <div className={`${styles['form-group']} ${styles['full-width']}`}>
                                <label><Hash size={16} /> Task Title <span className={styles['required']}>*</span></label>
                                <input
                                    type="text"
                                    name="title"
                                    className={styles['input-field']}
                                    value={formData.title}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div className={`${styles['form-group']} ${styles['full-width']}`}>
                                <label><MessageSquare size={16} /> Description</label>
                                <textarea
                                    name="description"
                                    className={styles['textarea-field']}
                                    value={formData.description}
                                    onChange={handleInputChange}
                                ></textarea>
                            </div>

                            <div className={styles['form-group']}>
                                <label><AlertCircle size={16} /> Status</label>
                                <CustomSelect
                                    options={[
                                        { value: 'open', label: 'Open' },
                                        { value: 'in_progress', label: 'In Progress' },
                                        { value: 'completed', label: 'Completed' },
                                        { value: 'overdue', label: 'Overdue' }
                                    ]}
                                    value={formData.status}
                                    onChange={(val) => setFormData(prev => ({ ...prev, status: val }))}
                                />
                            </div>

                            <div className={styles['form-group']}>
                                <label><Calendar size={16} /> Priority</label>
                                <CustomSelect
                                    options={[
                                        { value: 'high', label: 'High' },
                                        { value: 'medium', label: 'Medium' },
                                        { value: 'low', label: 'Low' }
                                    ]}
                                    value={formData.priority}
                                    onChange={(val) => setFormData(prev => ({ ...prev, priority: val }))}
                                />
                            </div>

                            <div className={styles['form-group']}>
                                <label><CheckCircle2 size={16} /> Due Date</label>
                                <CustomDatePicker
                                    selected={formData.due_date}
                                    onChange={(date) => setFormData(prev => ({ ...prev, due_date: date ? new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().split('T')[0] : '' }))}
                                    minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                                />
                            </div>

                            <div className={`${styles['form-group']} ${styles['full-width']}`}>
                                <label><Users size={16} /> Shared With Employees</label>
                                <PaginatedSelect
                                    endpoint="/employees-dropdown"
                                    valueKey="id"
                                    labelKey="employee_name"
                                    isMulti
                                    value={formData.employee_ids}
                                    onChange={(val) => setFormData(prev => ({ ...prev, employee_ids: val || [] }))}
                                    placeholder="Select Employees..."
                                />
                            </div>

                            <div className={styles['form-footer']}>
                                <button type="button" className={styles['btn-cancel']} onClick={() => navigate('/tasks')}>
                                    Cancel
                                </button>
                                <button type="submit" className={styles['btn-submit']} disabled={saving}>
                                    {saving ? <Loader2 className={styles['spinner']} /> : <Save size={18} />}
                                    <span>Update Task</span>
                                </button>
                            </div>
                        </form>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default EditTask;
